/**
 * CustomerTimesheet — read-only "hours at your sites" view for the
 * portal. Deep-linked from the Overview "Hours at your sites" tile.
 *
 * Shape: a per-day accordion of closed time entries (clockIn / clockOut
 * / hours / engineer / site / linked work order) with a running day
 * total. Data comes from `GET /api/customer/time-entries` which is
 * scoped server-side to the customer's org + any sub-customer sites.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Building2, User, ClipboardList, ChevronRight } from 'lucide-react';
import { PageHeader } from '../../components/Shared';
import { getCustomerTimeEntries } from '../../services/api';
import { formatDate } from '../../utils/date';

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch (e) {
    return '—';
  }
};

const fmtHours = (h) => `${Number(h || 0).toFixed(2)}h`;

const fmtDay = (iso) => formatDate(iso);

const CustomerTimesheet = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    getCustomerTimeEntries()
      .then(setEntries)
      .catch(e => setError(e?.response?.data?.detail || 'Could not load timesheet'))
      .finally(() => setLoading(false));
  }, []);

  // Group entries by `date` (yyyy-mm-dd). Only include CLOSED entries
  // so we're not showing engineers currently on the clock — those
  // aren't finalised yet.
  const grouped = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      if (!e.date || !e.clockOut) continue;
      if (!map.has(e.date)) map.set(e.date, []);
      map.get(e.date).push(e);
    }
    // Newest day first, entries within a day ordered by clockIn.
    return [...map.entries()]
      .sort((a, b) => b[0].localeCompare(a[0]))
      .map(([date, list]) => ({
        date,
        entries: list.sort((a, b) => (a.clockIn || '').localeCompare(b.clockIn || '')),
        total: list.reduce((s, e) => s + Number(e.hours || 0), 0),
      }));
  }, [entries]);

  const grandTotal = useMemo(
    () => grouped.reduce((s, d) => s + d.total, 0),
    [grouped],
  );

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="customer-timesheet-page">
      <PageHeader
        title="Hours at your sites"
        description="Every real clock-in / clock-out our engineers have logged on your sites. Grouped by day. This view intentionally does NOT include hours logged directly onto a work order — only physical time on site captured via the timeclock."
      />

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-500 text-sm">
          Loading timesheet…
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-md p-4">{error}</div>
      ) : grouped.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-md p-10 text-center text-slate-500 text-sm">
          <Clock className="h-8 w-8 mx-auto mb-2 text-slate-300" />
          No engineer hours logged on your sites yet.
        </div>
      ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-md p-4 flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">Total hours (all time)</div>
              <div className="text-3xl font-display text-slate-900">{fmtHours(grandTotal)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-slate-500">Days on site</div>
              <div className="text-3xl font-display text-slate-900">{grouped.length}</div>
            </div>
          </div>

          <div className="space-y-4">
            {grouped.map(day => (
              <div
                key={day.date}
                className="bg-white border border-slate-200 rounded-md overflow-hidden"
                data-testid={`timesheet-day-${day.date}`}
              >
                <div className="flex items-center justify-between bg-slate-50 border-b border-slate-200 px-4 py-3">
                  <div className="font-display uppercase tracking-wide text-sm text-slate-900">
                    {fmtDay(day.date)}
                  </div>
                  <div className="text-sm font-semibold text-blue-700">{fmtHours(day.total)}</div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-100">
                      <tr>
                        <th className="px-4 py-2">Engineer</th>
                        <th className="px-4 py-2">Site</th>
                        <th className="px-4 py-2">Clock in</th>
                        <th className="px-4 py-2">Clock out</th>
                        <th className="px-4 py-2 text-right">Hours</th>
                        <th className="px-4 py-2">Work order</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {day.entries.map(e => (
                        <tr key={e.id} data-testid={`timesheet-row-${e.id}`}>
                          <td className="px-4 py-3 text-slate-800">
                            <span className="inline-flex items-center gap-1.5">
                              <User className="h-3.5 w-3.5 text-slate-400" />
                              {e.userName || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-800">
                            <span className="inline-flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 text-slate-400" />
                              {e.siteName || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtTime(e.clockIn)}</td>
                          <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtTime(e.clockOut)}</td>
                          <td className="px-4 py-3 text-right font-medium text-slate-900">{fmtHours(e.hours)}</td>
                          <td className="px-4 py-3">
                            {e.workOrderId ? (
                              <Link
                                to={`/customer/work-orders/${e.workOrderId}`}
                                className="inline-flex items-center gap-1 text-xs font-mono text-blue-700 hover:text-blue-800"
                              >
                                <ClipboardList className="h-3.5 w-3.5" />
                                {e.workOrderId}
                                <ChevronRight className="h-3 w-3" />
                              </Link>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerTimesheet;
